// src/App.js
import React, { useEffect, Suspense, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
  useParams,
} from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop"

import Spinner from "./components/Spinner/Spinner";
import PublicFooter from "./components/PublicFooter/PublicFooter";
import PublicHeader from "./components/PublicHeader/PublicHeader";
import PublicPageFooter from "./components/PublicPageFooter/PublicPageFooter";
import RequireCompletedProfile from "./routes/RequireCompletedProfile";
import RequireStaff from "./routes/RequireStaff";
import RequireBlogAdmin from "./routes/RequireBlogAdmin";
import RequireEroticStoryAdmin from "./routes/RequireEroticStoryAdmin";
import "./App.css";
import { ToastProvider } from "./components/Toast/ToastProvider";
import { useMe } from "./hooks/useMe";
import {
  getStoredAuthTokens,
  getUser,
  hasStoredSessionIdentityHint,
  isAuthenticated,
} from "./utils/auth";
import {
  isClientBasicsComplete,
  resolveClientCompletionFlag,
} from "./utils/clientProfile";
import {
  isAnonymousPublicShellRoute,
  isAnonymousSessionOptionalRoute,
  isPublicShellInteractiveRoute,
  isPublicVirtualListingRoute,
  shouldShowPublicPageFooter,
} from "./utils/publicRouteMode";
import { normalizeReferralSource } from "./utils/referralLinks";
import { normalizeRoute, resolveRegistrationRoute } from "./utils/registration";
import { cacheCurrentUTMs } from "./utils/utmLandingStorage";
import lazyWithRetry from "./utils/lazyWithRetry";
import secondaryIntentRouting from "./constants/secondaryIntentRouting.js";
import HomePage from "./pages/HomePage/HomePage";
import RouteSeo from "./components/Seo/RouteSeo";
import RouteRobots from "./components/Seo/RouteRobots";

const lazyPage = (loader) => lazyWithRetry(loader);

const AppProviders = lazyPage(() => import("./AppProviders"));
const loadPublicShellProviders = () => import("./PublicShellProviders");
const PublicShellProviders = lazyPage(loadPublicShellProviders);
const NotificationProvider = lazyPage(() =>
  import("./context/NotificationContext").then((module) => ({
    default: module.NotificationProvider,
  }))
);
const GoogleServicesGate = lazyPage(() => import("./components/GoogleServicesGate"));
const Header = lazyPage(() => import("./components/Header/Header"));
const Footer = lazyPage(() => import("./components/Footer/Footer"));
const StoryBonusReminder = lazyPage(() => import("./components/StoryBonusReminder/StoryBonusReminder"));
const PaymentPlanReminder = lazyPage(() => import("./components/PaymentPlanReminder/PaymentPlanReminder"));
const ClientPromotionCampaignReminder = lazyPage(() =>
  import("./components/ClientPromotionCampaignReminder/ClientPromotionCampaignReminder")
);
const LoginPage = lazyPage(() => import("./pages/LoginPage/LoginPage"));
const SignupPage = lazyPage(() => import("./pages/SignupPage/SignupPage"));
const TermosDeUsoEPoliticaDePrivacidade = lazyPage(() =>
  import("./pages/TermosDeUsoEPoliticaDePrivacidade/TermosDeUsoEPoliticaDePrivacidade")
);
const loadProfilePageMusa = () => import("./pages/ProfilePageMusa/ProfilePageMusa");
if (typeof window !== "undefined") {
  const initialPath = window.location.pathname || "/";
  if (
    initialPath === "/perfilMusa" ||
    initialPath === "/musa/profile" ||
    initialPath.startsWith("/musa/")
  ) {
    loadProfilePageMusa();
  }
}
const ProfilePageMusa = lazyPage(loadProfilePageMusa);
const DashboardPage = lazyPage(() => import("./pages/DashboardPage/DashboardPage"));
const MuseDashboardPage = lazyPage(() => import("./pages/MuseDashboardPage/MuseDashboardPage"));
const ProfilePageCliente = lazyPage(() => import("./pages/ProfilePageCliente/ProfilePageCliente"));
const ClientProfileSetup = lazyPage(() => import("./pages/ClientProfileSetup/ClientProfileSetup"));
const ProfileSetupWizard = lazyPage(() => import("./pages/ProfileSetupWizard/ProfileSetupWizard"));
const CadastroMusa = lazyPage(() => import("./pages/SignupPage/CadastroMusa/CadastroMusa"));
const CadastroPhoneMusa = lazyPage(() =>
  import("./pages/SignupPage/CadastroMusa/CadastroPhoneMusa/CadastroPhoneMusa")
);
const CadastroPhoneReservaMusa = lazyPage(() =>
  import("./pages/SignupPage/CadastroMusa/CadastroPhoneMusa/CadastroPhoneReservaMusa")
);
const CadastroDadosESenhaMusa = lazyPage(() =>
  import("./pages/SignupPage/CadastroMusa/CadastroDadosESenhaMusa/CadastroDadosESenhaMusa")
);
const CadastroDocumentoMusa = lazyPage(() =>
  import("./pages/SignupPage/CadastroMusa/CadastroDocumentoMusa/CadastroDocumentoMusa")
);
const CadastroEmailCodeMusa = lazyPage(() =>
  import("./pages/SignupPage/CadastroMusa/CadastroEmailCodeMusa/CadastroEmailCodeMusa")
);
const ForgotPasswordRequest = lazyPage(() => import("./pages/ForgotPasswordRequest"));
const DeactivateAccount = lazyPage(() => import("./pages/DeactivateAccount/DeactivateAccount"));
const DeleteAccount = lazyPage(() => import("./pages/DeleteAccount/DeleteAccount"));
const DenunciasPage = lazyPage(() => import("./pages/DenunciasPage/DenunciasPage"));
const StoriesPage = lazyPage(() => import("./pages/StoriesPage/StoriesPage"));
const PaymentPage = lazyPage(() => import("./pages/PaymentPage/PaymentPage"));
const ContactSupport = lazyPage(() => import("./pages/ContactSupport/ContactSupport"));
const FavoritesPage = lazyPage(() => import("./pages/FavoritesPage/FavoritesPage"));
const ContentCreationPage = lazyPage(() =>
  import("./pages/ContentCreationPage/ContentCreationPage")
);
const ContentTimeline = lazyPage(() => import("./pages/ContentTimeline/ContentTimeline"));
const ComoFunciona = lazyPage(() => import("./pages/ComoFunciona/ComoFunciona"));
const SiteMapPage = lazyPage(() => import("./pages/SiteMapPage/SiteMapPage"));
const Blog = lazyPage(() => import("./pages/Blog/Blog"));
const BlogPost = lazyPage(() => import("./pages/BlogPost/BlogPost"));
const BlogAdminPage = lazyPage(() => import("./pages/BlogAdmin/BlogAdmin"));
const BlogEditorPage = lazyPage(() => import("./pages/BlogEditorPage/BlogEditorPage"));
const ContosEroticosPage = lazyPage(() => import("./pages/ContosEroticos/ContosEroticos"));
const ContoEroticoPage = lazyPage(() => import("./pages/ContoEroticoPage/ContoEroticoPage"));
const ContosEroticosAdminPage = lazyPage(() =>
  import("./pages/ContosEroticosAdmin/ContosEroticosAdmin")
);
const ContoEroticoEditorPage = lazyPage(() =>
  import("./pages/ContoEroticoEditorPage/ContoEroticoEditorPage")
);
const loadSearchPageMusa = () => import("./pages/SearchPageMusa/SearchPageMusa");
const SearchPageMusa = lazyPage(loadSearchPageMusa);
const VirtualCitySearchPage = lazyPage(() =>
  import("./pages/SearchPageMusa/VirtualCitySearchPage")
);
const VideochamadaLandingPage = lazyPage(() =>
  import("./pages/IntentLandingPages/VideochamadaLandingPage")
);
const ChatPrivadoLandingPage = lazyPage(() =>
  import("./pages/IntentLandingPages/ChatPrivadoLandingPage")
);
const ChamadaDeVozLandingPage = lazyPage(() =>
  import("./pages/IntentLandingPages/ChamadaDeVozLandingPage")
);
const ConteudoPersonalizadoLandingPage = lazyPage(() =>
  import("./pages/IntentLandingPages/ConteudoPersonalizadoLandingPage")
);
const AtendimentoDiscretoLandingPage = lazyPage(() =>
  import("./pages/IntentLandingPages/AtendimentoDiscretoLandingPage")
);
const PerfisVerificadosLandingPage = lazyPage(() =>
  import("./pages/IntentLandingPages/PerfisVerificadosLandingPage")
);
const SextingLandingPage = lazyPage(() =>
  import("./pages/IntentLandingPages/SextingLandingPage")
);
const RoleplayOnlineLandingPage = lazyPage(() =>
  import("./pages/IntentLandingPages/RoleplayOnlineLandingPage")
);
const FetichesOnlineLandingPage = lazyPage(() =>
  import("./pages/IntentLandingPages/FetichesOnlineLandingPage")
);
const IntentLandingBatch1Page = lazyPage(() =>
  import("./pages/IntentLandingPages/IntentLandingBatch1Page")
);
const IS_REACT_SNAP =
  typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

export function preloadAnonymousPublicRoute(pathname = "/") {
  const preloaders = [];

  if (isPublicShellInteractiveRoute(pathname)) {
    preloaders.push(loadPublicShellProviders());
  }

  if (isPublicVirtualListingRoute(pathname) || normalizeRoute(pathname) === "/indicacao") {
    preloaders.push(loadSearchPageMusa());
  }

  if (!preloaders.length) return Promise.resolve();
  return Promise.all(preloaders).catch(() => null);
}

/* ─────────────  routes that need a dark background  ───────────── */
const DARK_ROUTES = [
  "/cadastro-acompanhante",
  "/cadastro-email-musa",
  "/cadastro-musa-phone",
  "/cadastro-musa-phone-reserva",
  "/cadastro-dados-e-senha-musa",
  "/cadastro-documento-musa",
  "/cliente/profile-setup",
  "/cadastro",
  "/login",
  "/HomePage",
  "/denuncias",
  "/stories",
  "/pagamentos",
  "/home",
];

const NO_PROVIDER_ROUTES = [
  "/",
  "/home",
  "/HomePage",
  "/como-funciona",
  "/mapa-do-site",
  "/blog",
  "/contato",
  "/termos-de-uso-e-politica-de-privacidade",
  "/login",
  "/cadastro",
  "/recuperar-senha",
  "/cadastro-acompanhante",
  "/cadastro-email-musa",
  "/cadastro-musa-phone",
  "/cadastro-musa-phone-reserva",
  "/cadastro-dados-e-senha-musa",
  "/cadastro-documento-musa",
];

const BATCH_1_INTENT_ROUTES = (secondaryIntentRouting?.activeEntries || []).map((entry) =>
  String(entry?.path || "").replace(/\/+$/, "")
);

const LEGACY_BATCH_1_INTENT_REDIRECTS = Object.entries(
  secondaryIntentRouting?.legacyRedirects || {}
).map(([from, to]) => ({
  from: String(from || "").replace(/\/+$/, ""),
  to: String(to || "").replace(/\/+$/, ""),
}));

let sentryUtilsPromise = null;

function loadSentryUtils() {
  if (!sentryUtilsPromise) {
    sentryUtilsPromise = import("./utils/sentry").catch(() => null);
  }
  return sentryUtilsPromise;
}

function buildSentryErrorEntry(event) {
  const fallbackMessage = String(event?.message || "Window error").trim();
  return {
    error:
      event?.error instanceof Error ? event.error : new Error(fallbackMessage),
    context: {
      type: "error",
      filename: String(event?.filename || ""),
      lineno: Number(event?.lineno || 0),
      colno: Number(event?.colno || 0),
    },
  };
}

function buildSentryRejectionEntry(event) {
  const reason = event?.reason;
  return {
    error:
      reason instanceof Error
        ? reason
        : new Error(String(reason || "Unhandled promise rejection")),
    context: {
      type: "unhandledrejection",
    },
  };
}

function scheduleAnonymousPublicSentryOnSignal(options = {}) {
  if (typeof window === "undefined" || IS_REACT_SNAP) return undefined;

  const cleanups = [];
  let disposed = false;
  let interactionInitScheduled = false;

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    cleanups.splice(0).forEach((cleanup) => cleanup());
  };

  const addOneShotListener = (target, type, listener, listenerOptions) => {
    target.addEventListener(type, listener, listenerOptions);
    cleanups.push(() => target.removeEventListener(type, listener, listenerOptions));
  };

  const initAfterSignal = (signal, entry) => {
    if (disposed) return;
    dispose();

    loadSentryUtils().then((sentryUtils) => {
      if (!sentryUtils) return;

      if (entry && typeof sentryUtils.captureSentryPreinitError === "function") {
        sentryUtils.captureSentryPreinitError(entry, {
          pathname: options.pathname,
          hasAuthHint: options.hasAuthHint,
          forceFullInit: true,
          signal,
        });
        return;
      }

      sentryUtils.initSentry({
        pathname: options.pathname,
        hasAuthHint: options.hasAuthHint,
        allowReplay: signal === "interaction",
        signal,
      });
    });
  };

  const initAfterInteraction = () => {
    if (disposed || interactionInitScheduled) return;
    interactionInitScheduled = true;

    const run = () => {
      initAfterSignal("interaction");
    };

    const scheduleIdle = () => {
      if (disposed) return;
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(run, { timeout: 1800 });
        return;
      }
      window.setTimeout(run, 180);
    };

    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(scheduleIdle);
      });
      return;
    }

    scheduleIdle();
  };

  addOneShotListener(
    window,
    "error",
    (event) => initAfterSignal("error", buildSentryErrorEntry(event)),
    { once: true }
  );
  addOneShotListener(
    window,
    "unhandledrejection",
    (event) => initAfterSignal("unhandledrejection", buildSentryRejectionEntry(event)),
    { once: true }
  );
  addOneShotListener(
    window,
    "pointerdown",
    initAfterInteraction,
    { passive: true, once: true }
  );
  addOneShotListener(window, "keydown", initAfterInteraction, {
    once: true,
  });

  return dispose;
}

function scheduleSentryBootstrapAsync(options = {}) {
  const shouldWaitForPublicSignal =
    !options.immediate &&
    !options.hasAuthHint &&
    isAnonymousSessionOptionalRoute(options.pathname);

  if (shouldWaitForPublicSignal) {
    return scheduleAnonymousPublicSentryOnSignal(options);
  }

  loadSentryUtils().then((sentryUtils) => {
    sentryUtils?.scheduleSentryBootstrap(options);
  });

  return undefined;
}

function setSentryUserContextAsync(user) {
  loadSentryUtils().then((sentryUtils) => {
    sentryUtils?.setSentryUserContext(user || null);
  });
}


// Redireciona links antigos de documentação para o perfil da musa
function LegacyDocsRedirect({ me }) {
  const location = useLocation();
  const storedUser = getUser();
  const docStatus = (
    me?.progress_summary?.document_status ||
    me?.document_status ||
    storedUser?.progress_summary?.document_status ||
    storedUser?.document_status ||
    ""
  )
    .toString()
    .toLowerCase();

  const docRejectedOrNeedsResub =
    docStatus.includes("needs_resubmission") ||
    docStatus.includes("reprov") ||
    docStatus.includes("recus") ||
    docStatus.includes("rejeit");

  const incomingParams = new URLSearchParams(location.search || "");
  const reasonParam =
    incomingParams.get("reason") ||
    incomingParams.get("motivo") ||
    incomingParams.get("detail") ||
    incomingParams.get("message") ||
    "";
  const requestedRevalidation =
    incomingParams.get("revalidarDocs") === "1" ||
    incomingParams.get("revalidar") === "1" ||
    incomingParams.get("doc") === "revalidar";

  const shouldForceRevalidation =
    docRejectedOrNeedsResub || (!docStatus && (requestedRevalidation || Boolean(reasonParam)));

  const nextParams = new URLSearchParams();
  if (shouldForceRevalidation) {
    nextParams.set("revalidarDocs", "1");
    if (reasonParam) nextParams.set("reason", reasonParam);
  }

  const nextSearch = nextParams.toString();

  return (
    <Navigate
      to={{ pathname: "/perfilMusa", search: nextSearch ? `?${nextSearch}` : "" }}
      replace
      state={shouldForceRevalidation && reasonParam ? { reason: reasonParam } : undefined}
    />
  );
}

function RedirectPreservingQuery({ to, replace = true }) {
  const location = useLocation();
  const search = location.search || "";
  const target =
    typeof to === "string"
      ? { pathname: to, search }
      : { ...to, search: to.search ?? search };

  return <Navigate to={target} replace={replace} />;
}

function ReferralAliasRedirect() {
  const { source } = useParams();
  const normalizedSource = normalizeReferralSource(source);
  const search = normalizedSource ? `?utm_source=${encodeURIComponent(normalizedSource)}` : "";
  return <Navigate to={{ pathname: "/indicacao", search }} replace />;
}

/* ───────────────────────────────────────────── */
/*        COMPONENT THAT HOLDS THE ROUTES        */
/* ───────────────────────────────────────────── */
function useSharedShellEffects({ location, hasAuthHint, isAnonymousSessionOptionalPath, me }) {
  useEffect(() => {
    const mustBeDark = DARK_ROUTES.some((path) => location.pathname.startsWith(path));
    if (document.body.classList.contains("dark-bg") !== mustBeDark) {
      document.body.classList.toggle("dark-bg", mustBeDark);
    }
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = location.pathname || "/";
    if (
      currentPath === "/perfilMusa" ||
      currentPath === "/musa/profile" ||
      currentPath.startsWith("/musa/")
    ) {
      loadProfilePageMusa();
    }
  }, [location.pathname]);

  useEffect(() => {
    return scheduleSentryBootstrapAsync({
      pathname: location.pathname,
      hasAuthHint,
      immediate: !isAnonymousSessionOptionalPath || hasAuthHint,
    });
  }, [hasAuthHint, isAnonymousSessionOptionalPath, location.pathname]);

  useEffect(() => {
    if (!me && isAnonymousSessionOptionalPath && !hasAuthHint) {
      return undefined;
    }

    setSentryUserContextAsync(me);
    return undefined;
  }, [hasAuthHint, isAnonymousSessionOptionalPath, me]);

  useEffect(() => {
    if (IS_REACT_SNAP) return undefined;
    cacheCurrentUTMs({
      pathname: location.pathname,
      search: location.search,
    });
    return undefined;
  }, [location.pathname, location.search]);

}

function AppRouteTree({ me }) {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route
        path="/login"
        element={
          <GoogleServicesGate>
            <LoginPage />
          </GoogleServicesGate>
        }
      />
      <Route path="/recuperar-senha" element={<ForgotPasswordRequest />} />
      <Route
        path="/cadastro"
        element={
          <GoogleServicesGate>
            <SignupPage />
          </GoogleServicesGate>
        }
      />
      <Route
        path="/cadastro-cliente"
        element={<Navigate to="/cadastro/?tab=cliente" replace />}
      />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/cadastro/documentos" element={<LegacyDocsRedirect me={me} />} />
      <Route path="/cadastro/documento" element={<LegacyDocsRedirect me={me} />} />
      <Route path="/cadastro-documentos" element={<LegacyDocsRedirect me={me} />} />
      <Route path="/cadastro-documento" element={<LegacyDocsRedirect me={me} />} />
      <Route path="/cliente/:who" element={<ProfilePageCliente />} />
      <Route path="/perfilCliente" element={<ProfilePageCliente />} />
      <Route path="/musa/profile-setup/*" element={<ProfileSetupWizard />} />
      <Route path="/musa/:profileRef" element={<ProfilePageMusa />} />
      <Route element={<RequireCompletedProfile />}>
        <Route path="/musa/dashboard" element={<MuseDashboardPage />} />
        <Route path="/musa/profile" element={<ProfilePageMusa />} />
        <Route path="/perfilMusa" element={<ProfilePageMusa />} />
      </Route>
      <Route element={<RequireStaff />}>
        <Route path="/staff/musa/:id/dashboard" element={<MuseDashboardPage />} />
      </Route>
      <Route
        path="/cadastro-acompanhante"
        element={
          <ToastProvider>
            <GoogleServicesGate>
              <CadastroMusa />
            </GoogleServicesGate>
          </ToastProvider>
        }
      />
      <Route path="/cadastro-email-musa" element={<CadastroEmailCodeMusa />} />
      <Route
        path="/cadastro-musa-phone"
        element={
          <ToastProvider>
            <CadastroPhoneMusa />
          </ToastProvider>
        }
      />
      <Route
        path="/cadastro-musa-phone-reserva"
        element={
          <ToastProvider>
            <CadastroPhoneReservaMusa />
          </ToastProvider>
        }
      />
      <Route path="/cadastro-dados-e-senha-musa" element={<CadastroDadosESenhaMusa />} />
      <Route path="/cadastro-documento-musa" element={<CadastroDocumentoMusa />} />
      <Route path="/indicacao" element={<SearchPageMusa />} />
      <Route path="/indicacao/:source" element={<ReferralAliasRedirect />} />
      <Route path="/acompanhantes-virtuais" element={<SearchPageMusa />} />
      <Route
        path="/acompanhantes-virtuais/cidade/:citySlug"
        element={<VirtualCitySearchPage />}
      />
      <Route
        path="/acompanhantes-virtuais/videochamada"
        element={<VideochamadaLandingPage />}
      />
      <Route
        path="/acompanhantes-virtuais/chat-privado"
        element={<ChatPrivadoLandingPage />}
      />
      <Route
        path="/acompanhantes-virtuais/chamada-de-voz"
        element={<ChamadaDeVozLandingPage />}
      />
      <Route
        path="/acompanhantes-virtuais/conteudo-personalizado"
        element={<ConteudoPersonalizadoLandingPage />}
      />
      <Route
        path="/acompanhantes-virtuais/atendimento-discreto"
        element={<AtendimentoDiscretoLandingPage />}
      />
      <Route
        path="/acompanhantes-virtuais/perfis-verificados"
        element={<PerfisVerificadosLandingPage />}
      />
      <Route
        path="/acompanhantes-virtuais/sexting"
        element={<SextingLandingPage />}
      />
      <Route
        path="/acompanhantes-virtuais/roleplay-online"
        element={<RoleplayOnlineLandingPage />}
      />
      <Route
        path="/acompanhantes-virtuais/fetiches-online"
        element={<FetichesOnlineLandingPage />}
      />
      {LEGACY_BATCH_1_INTENT_REDIRECTS.map(({ from, to }) => (
        <Route
          key={from}
          path={from}
          element={<RedirectPreservingQuery to={to} />}
        />
      ))}
      {BATCH_1_INTENT_ROUTES.map((path) => (
        <Route key={path} path={path} element={<IntentLandingBatch1Page />} />
      ))}
      <Route
        path="/acompanhantes-virtuais/:categorySlug"
        element={<SearchPageMusa />}
      />
      <Route
        path="/acompanhantes-virtuais/*"
        element={<RedirectPreservingQuery to="/acompanhantes-virtuais/" />}
      />
      <Route path="/como-funciona" element={<ComoFunciona />} />
      <Route path="/mapa-do-site" element={<SiteMapPage />} />
      <Route path="/mapa-do-site/:section" element={<SiteMapPage />} />
      <Route path="/mapa-do-site/:section/pagina/:pageNumber" element={<SiteMapPage />} />
      <Route path="/cliente/profile-setup" element={<ClientProfileSetup />} />
      <Route path="/dados" element={<RedirectPreservingQuery to="/" />} />
      <Route path="/home" element={<RedirectPreservingQuery to="/" />} />
      <Route path="/HomePage" element={<RedirectPreservingQuery to="/" />} />
      <Route path="/deactivate-account" element={<DeactivateAccount />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/denuncias" element={<DenunciasPage />} />
      <Route path="/stories" element={<StoriesPage />} />
      <Route path="/conteudos" element={<ContentTimeline />} />
      <Route path="/conteudos/criar" element={<ContentCreationPage />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/tag/:tagSlug" element={<Blog />} />
      <Route path="/blog/categoria/:categorySlug" element={<Blog />} />
      <Route element={<RequireBlogAdmin />}>
        <Route path="/blog/admin" element={<BlogAdminPage />} />
        <Route path="/blog/admin/novo" element={<BlogEditorPage />} />
        <Route path="/blog/admin/:postId" element={<BlogEditorPage />} />
      </Route>
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/contos-eroticos" element={<ContosEroticosPage />} />
      <Route
        path="/contos-eroticos/categoria/:categorySlug"
        element={<ContosEroticosPage />}
      />
      <Route element={<RequireEroticStoryAdmin />}>
        <Route path="/contos-eroticos/admin" element={<ContosEroticosAdminPage />} />
        <Route path="/contos-eroticos/admin/novo" element={<ContoEroticoEditorPage />} />
        <Route path="/contos-eroticos/admin/:storyId" element={<ContoEroticoEditorPage />} />
      </Route>
      <Route path="/contos-eroticos/:slug" element={<ContoEroticoPage />} />
      <Route path="/pagamentos" element={<PaymentPage />} />
      <Route path="/favoritos" element={<FavoritesPage />} />
      <Route path="/contato" element={<ContactSupport />} />
      <Route
        path="/termos-de-uso-e-politica-de-privacidade"
        element={<TermosDeUsoEPoliticaDePrivacidade />}
      />
      <Route path="*" element={<RedirectPreservingQuery to="/" />} />
    </Routes>
  );
}

function ShellFrame({
  me,
  meLoading = false,
  providerMode = "none",
  enableNotifications = false,
  shellVariant = "auth",
  header = null,
  reminders = null,
  footer = null,
  showPublicPageFooter = false,
  contentClassNames = ["App-content"],
  wrapRoutesInSuspense = true,
}) {
  const contentClasses = contentClassNames.filter(Boolean).join(" ");
  const routeTree = <AppRouteTree me={me} />;
  const appShell = (
    <div className="App">
      <div className={`App-shell App-shell--${shellVariant}`}>
        <RouteSeo />
        <RouteRobots />
        {header}
        {reminders}
        <main className={contentClasses}>
          {wrapRoutesInSuspense ? (
            <Suspense
              fallback={
                <div
                  className={`App-route-fallback${
                    shellVariant === "public" ? " App-route-fallback--public" : ""
                  }`}
                >
                  <Spinner />
                </div>
              }
            >
              {routeTree}
            </Suspense>
          ) : (
            routeTree
          )}
          {showPublicPageFooter && <PublicPageFooter />}
        </main>
        {footer}
      </div>
    </div>
  );
  const notificationShell = enableNotifications ? (
    <Suspense fallback={<div className="App"><Spinner /></div>}>
      <NotificationProvider currentUser={me}>{appShell}</NotificationProvider>
    </Suspense>
  ) : (
    appShell
  );

  if (providerMode === "public") {
    return (
      <Suspense fallback={<div className="App"><Spinner /></div>}>
        <PublicShellProviders>{notificationShell}</PublicShellProviders>
      </Suspense>
    );
  }

  if (providerMode === "full") {
    return (
      <Suspense fallback={<div className="App"><Spinner /></div>}>
        <AppProviders me={me} meLoading={meLoading}>
          {notificationShell}
        </AppProviders>
      </Suspense>
    );
  }

  return notificationShell;
}

function AnonymousPublicShell({ location }) {
  useSharedShellEffects({
    location,
    hasAuthHint: false,
    isAnonymousSessionOptionalPath: true,
    me: null,
  });

  const shouldUsePublicProviders = isPublicShellInteractiveRoute(location.pathname);
  const isStaticHomeRoute = normalizeRoute(location.pathname) === "/";
  const showPublicPageFooter = shouldShowPublicPageFooter(location.pathname);

  return (
    <ShellFrame
      me={null}
      providerMode={shouldUsePublicProviders ? "public" : "none"}
      shellVariant="public"
      header={<PublicHeader />}
      footer={<PublicFooter />}
      showPublicPageFooter={showPublicPageFooter}
      contentClassNames={["App-content", "App-content--public", "has-public-footer"]}
      wrapRoutesInSuspense={!isStaticHomeRoute}
    />
  );
}

function AuthenticatedAppShell({
  location,
  hasAuthHint,
  forceAnonymousProbe = false,
}) {
  const navigate = useNavigate();
  const isAnonymousSessionOptionalPath = isAnonymousSessionOptionalRoute(location.pathname);
  const shouldAllowAnonymousMeProbe = !isAnonymousSessionOptionalPath || hasAuthHint;
  const { me, isLoading: meLoading, refetch: refetchMe } = useMe({
    allowAnonymousProbe: shouldAllowAnonymousMeProbe,
    deferAnonymousProbe: isAnonymousSessionOptionalPath && !hasAuthHint,
    forceAnonymousProbe,
  });
  const [isPaymentPlanReminderOpen, setIsPaymentPlanReminderOpen] = useState(false);
  const [isClientPromotionReminderOpen, setIsClientPromotionReminderOpen] =
    useState(false);
  const [shouldRenderFooter, setShouldRenderFooter] = useState(() => true);

  useSharedShellEffects({
    location,
    hasAuthHint,
    isAnonymousSessionOptionalPath,
    me,
  });

  // Bloqueia menu de contexto em imagens/vídeos e remove botão de download dos players
  useEffect(() => {
    const mediaSelector = "video, img, picture";

    const preventMediaContextMenu = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(mediaSelector)) {
        event.preventDefault();
      }
    };

    const protectVideo = (video) => {
      if (!(video instanceof HTMLVideoElement)) return;
      const current = (video.getAttribute("controlsList") || "").trim();
      const tokens = current ? current.split(/\s+/) : [];
      if (!tokens.includes("nodownload")) {
        tokens.push("nodownload");
        video.setAttribute("controlsList", tokens.join(" "));
      }
    };

    const protectVideosIn = (root = document) => {
      if (!root || typeof root.querySelectorAll !== "function") return;
      root.querySelectorAll("video").forEach(protectVideo);
    };

    protectVideosIn();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node instanceof HTMLVideoElement) {
            protectVideo(node);
          } else {
            protectVideosIn(node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener("contextmenu", preventMediaContextMenu);

    return () => {
      document.removeEventListener("contextmenu", preventMediaContextMenu);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) return;
    if (meLoading) return;

    const storedUser = getUser();
    const coerceBool = (value) => {
      if (typeof value === "string") return value.trim().toLowerCase() === "true";
      return Boolean(value);
    };
    const progressFrom = (userLike) =>
      userLike?.progress_summary?.progress_step ?? userLike?.progress_step ?? null;
    const normalizeEmail = (value = "") => {
      if (typeof value !== "string") return "";
      return value.trim().toLowerCase();
    };
    const resolveUserEmail = (userLike) => {
      const candidates = [
        userLike?.email,
        userLike?.username,
        userLike?.user?.email,
        userLike?.user?.username,
        userLike?.progress_summary?.email,
        userLike?.progress_summary?.username,
      ];
      return candidates.map(normalizeEmail).find(Boolean) || "";
    };
    const resolveReserveStepOverride = (userLike) => {
      const override = localStorage.getItem("musa_needs_reserve_step");
      if (override !== "false") return undefined;

      const currentUserEmail = resolveUserEmail(userLike);
      if (!currentUserEmail) return undefined;

      const localSessionEmail = normalizeEmail(
        localStorage.getItem("musa_email") || localStorage.getItem("userEmail") || ""
      );

      if (localSessionEmail && localSessionEmail !== currentUserEmail) {
        return undefined;
      }

      return true;
    };

    const meCompleted = coerceBool(me?.profile_completed ?? me?.progress_summary?.profile_completed);
    const storedCompleted = coerceBool(
      storedUser?.profile_completed ?? storedUser?.progress_summary?.profile_completed
    );
    const meProgress = progressFrom(me) ?? 0;
    const storedProgress = progressFrom(storedUser) ?? 0;

    const preferStoredUser =
      storedUser && ((!meCompleted && storedCompleted) || storedProgress > meProgress);

    const baseUser = (() => {
      if (preferStoredUser && me) return { ...me, ...storedUser };
      return preferStoredUser ? storedUser : me || storedUser;
    })();
    const reserveStepDoneOverride = resolveReserveStepOverride(baseUser);
    const routingUser = (() => {
      if (reserveStepDoneOverride !== true) return baseUser;

      const baseProgressSummary = baseUser?.progress_summary || {};
      return {
        ...baseUser,
        reserve_phone_step_done: true,
        progress_summary: {
          ...baseProgressSummary,
          reserve_phone_step_done: true,
        },
      };
    })();

    if (!routingUser) return;

    const enrichedUser = routingUser.role
      ? routingUser
      : storedUser?.role
      ? { ...routingUser, role: storedUser.role }
      : routingUser;
    const targetRoute = resolveRegistrationRoute(enrichedUser);
    if (!targetRoute) return;

    const currentPath = normalizeRoute(location.pathname);
    const requiredPath = normalizeRoute(targetRoute);
    const docStatus = (
      enrichedUser?.progress_summary?.document_status ||
      enrichedUser?.document_status ||
      ""
    ).toString().toLowerCase();
    const docRejected =
      enrichedUser?.progress_summary?.document_rejected ||
      enrichedUser?.document_rejected ||
      docStatus.includes("needs_resubmission") ||
      docStatus.includes("reprov") ||
      docStatus.includes("recus") ||
      docStatus.includes("rejeit");

    if (currentPath !== requiredPath && !(docRejected && requiredPath === "/cadastro-documento-musa")) {
      const email =
        baseUser.email ||
        baseUser.username ||
        localStorage.getItem("musa_email") ||
        localStorage.getItem("userEmail") ||
        "";

      if (email) {
        localStorage.setItem("musa_email", email);
      }

      const googleFlag = (() => {
        const flag = baseUser.registered_with_google;
        if (typeof flag === "boolean") return flag;

        const providers = [
          baseUser.provider,
          baseUser.auth_provider,
          baseUser.login_via,
        ].filter(Boolean);

        if (providers.some((value) => String(value).toLowerCase() === "google")) {
          return true;
        }

        return localStorage.getItem("isGoogleSignedIn") === "true";
      })();

      if (googleFlag) {
        localStorage.setItem("isGoogleSignedIn", "true");
      }

      navigate(targetRoute, {
        replace: true,
        state: { email, isGoogleSignedIn: googleFlag },
      });
    }
  }, [location.pathname, me, meLoading, navigate]);

  useEffect(() => {
    if (!isAuthenticated()) return;
    if (meLoading) return;

    const storedUser = getUser();
    const baseUser = me || storedUser;
    if (!baseUser) return;

    const rawRole = baseUser.role || storedUser?.role || baseUser.user_type || baseUser.account_type;
    const role = typeof rawRole === "string" ? rawRole.toLowerCase() : rawRole;
    if (role !== "cliente") return;

    const completionFlag =
      resolveClientCompletionFlag(baseUser) ?? resolveClientCompletionFlag(storedUser);
    const isComplete =
      completionFlag !== null
        ? completionFlag
        : isClientBasicsComplete(baseUser) || isClientBasicsComplete(storedUser);

    const currentPath = normalizeRoute(location.pathname);
    const setupPath = normalizeRoute("/cliente/profile-setup");
    const profilePath = normalizeRoute("/perfilCliente");
    if (isComplete) {
      if (currentPath === setupPath) {
        navigate(profilePath, { replace: true });
      }
      return;
    }

    if (currentPath === setupPath) return;
    navigate(setupPath, { replace: true });
  }, [location.pathname, me, meLoading, navigate]);

  const hideHeaderRoutes = ["/conteudos"];

  const normalizedRole = (me?.role || getUser()?.role || "").toLowerCase();
  const isCliente =
    normalizedRole === "cliente" ||
    normalizedRole === "client" ||
    normalizedRole.includes("cliente");
  const isMusa =
    normalizedRole.includes("musa") ||
    normalizedRole.includes("acompanhante") ||
    normalizedRole.includes("escort");

  const hideFooterRoutes = [
    "/login",
    "/signup",
    "/cadastro",
    "/perfilMusa",
    "/perfilCliente",
    "/cliente/profile-setup",
    "/musa/",
    "/musa/profile",
    "/musa/profile-setup",
    "/deactivate-account",
    "/denuncias",
    "/stories",
    "/pagamentos",
    "/conteudos",
    "/conteudos/criar",
    "/staff/",
  ];
  const musaProfileIdMatch = location.pathname.match(/^\/musa\/(?:[a-z0-9-]+-)?(\d+)(?:\/)?$/i);
  const isOwnMusaProfilePage =
    isMusa &&
    !location.pathname.startsWith("/musa/profile-setup") &&
    (location.pathname === "/perfilMusa" ||
      location.pathname === "/musa/profile" ||
      (musaProfileIdMatch && me?.id && String(musaProfileIdMatch[1]) === String(me.id)));
  const isPublicMusaProfilePage =
    Boolean(musaProfileIdMatch) &&
    (!me?.id || String(musaProfileIdMatch[1]) !== String(me.id));
  const isOwnClientProfilePage = isCliente && location.pathname.startsWith("/perfilCliente");

  const shouldShowFooter =
    isOwnClientProfilePage ||
    isOwnMusaProfilePage ||
    isPublicMusaProfilePage ||
    !hideFooterRoutes.some((route) => location.pathname.startsWith(route));
  const shouldShowHeader = !hideHeaderRoutes.some((route) => location.pathname.startsWith(route));
  const shouldMountReminders = isAuthenticated();
  const shouldDeferFooter =
    !IS_REACT_SNAP &&
    location.pathname.startsWith("/acompanhantes-virtuais") &&
    (() => {
      if (typeof window === "undefined") return true;
      try {
        const isMobile = window.matchMedia
          ? window.matchMedia("(max-width: 768px)").matches
          : window.innerWidth <= 768;
        const saveData = Boolean(navigator?.connection?.saveData);
        return isMobile || saveData;
      } catch (_) {
        return true;
      }
    })();

  const shouldUseProviders = !NO_PROVIDER_ROUTES.some((route) => {
    if (route === "/") return location.pathname === "/";
    return location.pathname.startsWith(route);
  });
  const shouldUseNotificationProvider = !IS_REACT_SNAP && isAuthenticated();
  const showPublicPageFooter = shouldShowPublicPageFooter(location.pathname);

  useEffect(() => {
    if (!shouldShowFooter || !shouldDeferFooter) {
      setShouldRenderFooter(shouldShowFooter);
      return undefined;
    }

    setShouldRenderFooter(false);

    let idleId;
    let timeoutId;
    const revealFooter = () => setShouldRenderFooter(true);

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(revealFooter, { timeout: 1800 });
    } else if (typeof window !== "undefined") {
      timeoutId = window.setTimeout(revealFooter, 1200);
    }

    return () => {
      if (idleId && typeof window !== "undefined" && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [shouldDeferFooter, shouldShowFooter]);

  const appContentClasses = ["App-content"];
  if (shouldShowFooter) appContentClasses.push("has-footer");
  if (shouldShowHeader) appContentClasses.push("has-header");

  const reminders = shouldMountReminders ? (
    <>
      <Suspense fallback={null}>
        <PaymentPlanReminder
          me={me}
          meLoading={meLoading}
          refetchMe={refetchMe}
          onVisibilityChange={setIsPaymentPlanReminderOpen}
        />
      </Suspense>
      {!isPaymentPlanReminderOpen && (
        <Suspense fallback={null}>
          <ClientPromotionCampaignReminder
            me={me}
            meLoading={meLoading}
            onVisibilityChange={setIsClientPromotionReminderOpen}
          />
        </Suspense>
      )}
      {!isPaymentPlanReminderOpen && !isClientPromotionReminderOpen && (
        <Suspense fallback={null}>
          <StoryBonusReminder me={me} meLoading={meLoading} />
        </Suspense>
      )}
    </>
  ) : null;

  const header = shouldShowHeader ? (
    <Suspense fallback={null}>
      <Header enableNotifications={shouldUseNotificationProvider} />
    </Suspense>
  ) : null;

  const footer = shouldShowFooter && shouldRenderFooter ? (
    <Suspense fallback={null}>
      <Footer currentUser={me} />
    </Suspense>
  ) : null;
  const isRecoveringSessionShell = forceAnonymousProbe && meLoading && !me;

  return (
    <ShellFrame
      me={me}
      meLoading={meLoading}
      providerMode={shouldUseProviders ? "full" : "none"}
      enableNotifications={shouldUseNotificationProvider}
      shellVariant="auth"
      header={isRecoveringSessionShell ? null : header}
      reminders={reminders}
      footer={isRecoveringSessionShell ? null : footer}
      showPublicPageFooter={isRecoveringSessionShell ? false : showPublicPageFooter}
      contentClassNames={appContentClasses}
    />
  );
}

function AppContent() {
  const location = useLocation();
  const cachedUserSnapshot = getUser();
  const { accessToken, refreshToken } = getStoredAuthTokens();
  const hasStoredSessionHint = hasStoredSessionIdentityHint();
  const hasAuthHint = Boolean(cachedUserSnapshot || accessToken || refreshToken);
  const shouldRecoverCookieSession =
    isAnonymousPublicShellRoute(location.pathname) &&
    !hasAuthHint &&
    hasStoredSessionHint;
  const shouldUseAnonymousPublicShell =
    isAnonymousPublicShellRoute(location.pathname) &&
    !hasAuthHint &&
    !shouldRecoverCookieSession;

  if (shouldUseAnonymousPublicShell) {
    return <AnonymousPublicShell location={location} />;
  }

  return (
    <AuthenticatedAppShell
      location={location}
      hasAuthHint={hasAuthHint}
      forceAnonymousProbe={shouldRecoverCookieSession}
    />
  );
}

export default function App() {
  return (
    <Router>
      <ScrollToTop />   
      <AppContent />
    </Router>
  );
}
